import type {
  LayMinesAction,
  MoveAction,
  OblivionMineAction,
  OblivionMineState,
  ValidationResult,
} from "../types";
import { OBLIVION_MINE_MINES_PER_PLAYER } from "../types";
import {
  isInBounds,
  isMineableCell,
  isOrthogonalStep,
  isPlayerId,
  positionKey,
} from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: OblivionMineState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) {
    return fail("Unknown player", { playerId });
  }
  if (state.phase === "game-over") {
    return fail("Game is already over");
  }
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

export function validateLayMines(
  state: OblivionMineState,
  action: LayMinesAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "lay-mines") {
    return fail("Mines can only be laid during the lay-mines phase", {
      phase: state.phase,
    });
  }

  if (state.minesLaid[action.playerId]) {
    return fail("This player has already laid their mines", {
      playerId: action.playerId,
    });
  }
  if (action.cells.length !== OBLIVION_MINE_MINES_PER_PLAYER) {
    return fail(`Must lay exactly ${OBLIVION_MINE_MINES_PER_PLAYER} mines`, {
      count: action.cells.length,
    });
  }

  const seen = new Set<string>();
  for (const cell of action.cells) {
    if (!isInBounds(cell.row, cell.col)) {
      return fail("Mine is outside the board", { cell });
    }
    if (!isMineableCell(cell.row, cell.col)) {
      return fail("Cannot lay a mine on a start or treasure cell", { cell });
    }
    const key = positionKey(cell);
    if (seen.has(key)) {
      return fail("Duplicate mine cell", { cell });
    }
    seen.add(key);
  }
  return ok;
}

export function validateMove(
  state: OblivionMineState,
  action: MoveAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;
  if (state.phase !== "move") {
    return fail("Pieces can only move during the move phase", {
      phase: state.phase,
    });
  }

  const { to } = action;
  if (!isInBounds(to.row, to.col)) {
    return fail("Destination is outside the board", { to });
  }
  const from = state.positions[action.playerId];
  if (!isOrthogonalStep(from, to)) {
    return fail("Move must be one orthogonal step", { from, to });
  }
  return ok;
}

export function validateAction(
  state: OblivionMineState,
  action: OblivionMineAction,
): ValidationResult {
  switch (action.type) {
    case "LayMines":
      return validateLayMines(state, action);
    case "Move":
      return validateMove(state, action);
  }
}
